import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_SLIDES } from "../lib/content";
import { motion, AnimatePresence } from "framer-motion";

export default function HeroSlider() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    const id = setInterval(() => emblaApi.scrollNext(), 6500);
    return () => clearInterval(id);
  }, [emblaApi, onSelect]);

  return (
    <section
      className="relative w-full h-[100vh] min-h-[680px] bg-[#0A1F3F] text-white overflow-hidden"
      data-testid="hero-slider"
    >
      <div className="absolute inset-0" ref={emblaRef}>
        <div className="flex h-full">
          {HERO_SLIDES.map((slide, i) => (
            <div
              key={i}
              className="relative shrink-0 grow-0 basis-full h-full"
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F3F]/95 via-[#0A1F3F]/70 to-[#0A1F3F]/30" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-28 pt-40">
        <AnimatePresence mode="wait">
          <motion.div
            key={selected}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#F37021]" />
              <span className="text-eyebrow text-[#F37021]" data-testid="hero-eyebrow">
                {HERO_SLIDES[selected].eyebrow}
              </span>
            </div>
            <h1
              className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.92] whitespace-pre-line"
              data-testid="hero-title"
            >
              {HERO_SLIDES[selected].title}
            </h1>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed" data-testid="hero-subtitle">
              {HERO_SLIDES[selected].subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              {HERO_SLIDES[selected].ctas.map((cta, idx) => (
                <Link
                  key={idx}
                  to={cta.to}
                  data-testid={`hero-cta-${idx}`}
                  className={`inline-flex items-center gap-3 px-8 py-5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${
                    cta.primary
                      ? "bg-[#F37021] hover:bg-[#D35F16] text-white"
                      : "border border-white/50 hover:bg-white hover:text-[#0A1F3F] text-white"
                  }`}
                >
                  {cta.label}
                  {cta.primary ? (
                    <ArrowRight className="w-4 h-4 arrow-icon" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4" />
                  )}
                </Link>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-16 flex items-end justify-between">
          <div className="flex gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                className={`h-[2px] transition-all duration-500 ${
                  selected === i ? "w-16 bg-[#F37021]" : "w-8 bg-white/30"
                }`}
              />
            ))}
          </div>
          <div className="hidden md:block text-right">
            <div className="text-eyebrow text-white/60">Slide</div>
            <div className="font-display text-3xl font-black tracking-tight mt-1">
              {String(selected + 1).padStart(2, "0")}
              <span className="text-white/40"> / {String(HERO_SLIDES.length).padStart(2, "0")}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
